/**
 * KCIS Admin → University → University Settings → Educational Philosophy
 *
 * Institution-wide instructional philosophy, inherited by every course
 * regardless of instructor. Kept separate from Instructor AI Profiles.
 * Injected into every KAPE LLM call as a distinct layer alongside the
 * instructor profile block.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

export default function EducationalPhilosophy() {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kcis/admin/university/settings/educational-philosophy");
      setDoc(data.philosophy);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const setList = (k, i, v) => setDoc((d) => {
    const arr = [...(d?.[k] || [])]; arr[i] = v; return { ...d, [k]: arr };
  });
  const addItem = (k) => setDoc((d) => ({ ...d, [k]: [...(d?.[k] || []), ""] }));
  const removeItem = (k, i) => setDoc((d) => ({ ...d, [k]: (d[k] || []).filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/kcis/admin/university/settings/educational-philosophy", {
        positioning: doc.positioning,
        principles: (doc.principles || []).filter((s) => s.trim()),
        institutional_standards: (doc.institutional_standards || []).filter((s) => s.trim()),
        instructional_framework: (doc.instructional_framework || []).filter((s) => s.trim()),
        generation_directives: (doc.generation_directives || []).filter((s) => s.trim()),
      });
      toast.success("Educational Philosophy saved · inherited by every course");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!doc) {
    return <KcisAdminShell><div className="p-6 text-sm" style={{ color: PAL.muted }}>Loading…</div></KcisAdminShell>;
  }

  return (
    <KcisAdminShell>
      <div className="p-6 space-y-5" style={{ background: PAL.bg, color: PAL.text, minHeight: "100vh" }}
           data-testid="educational-philosophy-page">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
            KCIS Admin · University Settings
          </div>
          <h1 className="text-2xl font-semibold mt-1">Educational Philosophy</h1>
          <div className="text-sm mt-1" style={{ color: PAL.muted }}>
            Institution-wide instructional philosophy · version {doc.version} · updated {doc.updated_at?.slice(0, 16)}
            · inherited by every course as a distinct layer above the instructor profile.
          </div>
        </div>

        <Section title="Positioning">
          <textarea value={doc.positioning || ""}
                     onChange={(e) => setField("positioning", e.target.value)}
                     rows={3}
                     className="w-full rounded p-3 text-sm bg-transparent border"
                     style={{ borderColor: PAL.border, color: PAL.text }}
                     data-testid="phi-positioning" />
        </Section>

        <ListSection title="Principles" k="principles" doc={doc}
                        setList={setList} addItem={addItem} removeItem={removeItem} />
        <ListSection title="Institutional Standards" k="institutional_standards" doc={doc}
                        setList={setList} addItem={addItem} removeItem={removeItem} />
        <ListSection title="Instructional Framework (order matters)" k="instructional_framework" doc={doc}
                        setList={setList} addItem={addItem} removeItem={removeItem} />
        <ListSection title="Generation Directives" k="generation_directives" doc={doc}
                        setList={setList} addItem={addItem} removeItem={removeItem} />

        <div className="flex justify-end">
          <button onClick={save} disabled={saving}
                  className="text-sm rounded-full px-5 py-2 font-semibold disabled:opacity-40"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid="phi-save">
            {saving ? "Saving…" : "Save philosophy"}
          </button>
        </div>
      </div>
    </KcisAdminShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: PAL.border, background: PAL.panel }}>
      <div className="text-sm font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function ListSection({ title, k, doc, setList, addItem, removeItem }) {
  const items = doc[k] || [];
  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid={`phi-section-${k}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">{title}</div>
        <button onClick={() => addItem(k)}
                className="text-xs rounded-full px-3 py-1 border"
                style={{ borderColor: PAL.border, color: PAL.accent }}
                data-testid={`phi-add-${k}`}>+ Add</button>
      </div>
      {items.length === 0 && <div className="text-xs" style={{ color: PAL.muted }}>No items.</div>}
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 mt-1">
          <input value={item} onChange={(e) => setList(k, i, e.target.value)}
                 className="flex-1 rounded p-2 text-sm bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid={`phi-${k}-${i}`} />
          <button onClick={() => removeItem(k, i)}
                  className="text-xs rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: "#F87171" }}
                  data-testid={`phi-remove-${k}-${i}`}>×</button>
        </div>
      ))}
    </div>
  );
}
